type EventKey = string | symbol
type EventHandler<T = any> = (payload: T) => void
type Bus<E> = Record<keyof E, E[keyof E][]>
export type EventMap = Record<EventKey, EventHandler>

export interface EventBus<T extends EventMap> {
  on<Key extends keyof T>(key: Key, handler: T[Key]): () => void
  destroy<Key extends keyof T>(key: Key, handler: T[Key]): void
  emit<Key extends keyof T>(key: Key, ...payload: Parameters<T[Key]>): void
  once<Key extends keyof T>(key: Key, handler: T[Key]): void
}

export function eventbus<E extends EventMap>(config?: {
  // error handler for later
  onError: (...params: any[]) => void
}): EventBus<E> {
  const bus: Partial<Bus<E>> = {}

  const on: EventBus<E>['on'] = (key, handler) => {
    if (bus[key] === undefined) {
      bus[key] = []
    }
    bus[key]?.push(handler)

    // unsubscribe function
    return () => {
      destroy(key, handler)
    }
  }

  const destroy: EventBus<E>['destroy'] = (key, handler) => {
    const index = bus[key]?.indexOf(handler) ?? -1
    bus[key]?.splice(index >>> 0, 1)
  }

  const emit: EventBus<E>['emit'] = (key, ...payload) => {

    console.groupCollapsed(key)
    payload.forEach((item) => console.log(item))
    console.groupEnd()

    bus[key]?.forEach((fn) => {
      try {
        // @ts-ignore
        fn(...payload)
      } catch (e) {
        config?.onError(e)
      }
    })
  
    bus["*"]?.forEach((fn) => {
      try {
        // This is an interceptor handler for all (*) events thus
        // its payload is modified to include the event key
        // not sure if this is a good idea but it's here for now
        fn({ key, payload })
      } catch (e) {
        config?.onError(e)
      }
    })
  }

  const once: EventBus<E>['once'] = (key, handler) => {
    const handleOnce = (payload: Parameters<typeof handler>) => {
      handler(payload)
      destroy(key, handleOnce as typeof handler)
    }

    on(key, handleOnce as typeof handler)
  }

  return {
    on,
    destroy,
    emit,
    once,
  }
}
